/**
 * Deep-link registry — the public contract between marketing and the app.
 *
 * Marketing is given short, stable, customer-facing links of the form:
 *
 *     https://www.pensionbee.com/app/<slug>
 *
 * Each `<slug>` is mapped here to the *current* internal route. Because the
 * slug is the contract, we can freely move Next.js pages around (change the
 * `path` values below) without ever asking marketing to update the links they
 * have baked into Iterable email / push / in-app campaigns. This file is the
 * ONLY place that needs to change when a feature's route moves.
 *
 * Keep slugs:
 *   - short and human-readable (they appear in customer-facing URLs),
 *   - kebab-case,
 *   - stable once published to marketing (renaming a slug breaks live links).
 *
 * `path` is the internal route passed to `router.push()` — WITHOUT the `/uk`
 * basePath (see CLAUDE.md routing notes). Always absolute, never relative.
 */

/** Path segment that namespaces all app deep links: `/app/<slug>`.
 *  Scoping to a prefix is what lets Universal Links / App Links claim only
 *  these paths instead of hijacking the entire pensionbee.com website. */
export const DEEP_LINK_PREFIX = 'app';

export interface DeepLinkTarget {
  /** Internal route for `router.push()` (no `/uk` basePath, always absolute). */
  path: string;
  /** Human-friendly feature name, used to generate the approved-links list. */
  label: string;
}

export const deepLinkRegistry = {
  contributions: {
    path: '/hive/beehive/actions/contribute',
    label: 'Contributions',
  },
  // The transfer journey computes its first stage at runtime (see
  // useExistingPensionHandler), so there is no safe static mid-journey URL to
  // deep-link into. We land the user on the Actions tab where the journey
  // starts. Follow-up: a dedicated launcher route could start it in one tap.
  'add-a-transfer': {
    path: '/hive/transfers/summary',
    label: 'Add a transfer',
  },
  'transfer-tracker': {
    path: '/hive/transfers/tracker',
    label: 'Transfer tracker',
  },
  summary: {
    path: '/hive/beehive/summary',
    label: 'Summary tab',
  },
  'my-pension': {
    path: '/hive/beehive/my-pension',
    label: 'My Pension tab',
  },
  actions: {
    path: '/hive/beehive/actions',
    label: 'Actions tab',
  },
  discover: {
    path: '/hive/beehive/discover',
    label: 'Discover tab',
  },
  account: {
    path: '/hive/beehive/account',
    label: 'Account tab',
  },
  support: {
    path: '/hive/beehive/account/support',
    label: 'Support',
  },
  faqs: {
    path: '/hive/beehive/account/faq',
    label: 'FAQs',
  },
  'my-profile': {
    path: '/hive/beehive/account/my-profile',
    label: 'My profile',
  },
  documents: {
    path: '/hive/beehive/account/documents-resources',
    label: 'Documents and resources',
  },
  'refer-a-friend': {
    path: '/hive/beehive/actions/refer-a-friend',
    label: 'Refer a friend (RAF)',
  },
  beneficiaries: {
    path: '/hive/beehive/account/beneficiaries',
    label: 'Beneficiaries',
  },
  withdrawals: {
    path: '/hive/beehive/actions/withdraw',
    label: 'Withdrawals',
  },
  'plan-info': {
    path: '/hive/beehive/my-pension/my-plan',
    label: 'Plan information',
  },
  'switch-plans': {
    path: '/hive/beehive/actions/switch-plan',
    label: 'Switch plans',
  },
  'retirement-planner': {
    path: '/hive/beehive/my-pension/retirement-planner',
    label: 'Retirement planner',
  },
  planning: {
    path: '/hive/beehive/elevated-retirement-planner',
    label: 'Planning tab',
  },
} as const satisfies Record<string, DeepLinkTarget>;

export type DeepLinkSlug = keyof typeof deepLinkRegistry;
